import { Head, Link, router, useForm, usePage } from "@inertiajs/react";
import { ArrowLeft, Copy, Link as LinkIcon, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import InputError from "@/components/input-error";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { useOrganizationRoutes } from "@/hooks/use-organization-routes";
import AppLayout from "@/layouts/app-layout";
import type { BreadcrumbItem, Organization, SharedData } from "@/types";

interface GroupOption {
  key: string;
  name: string;
}

interface MemberDetail {
  id: number;
  email: string;
  name?: string | null;
  full_name?: string | null;
  groups: string[];
}

interface MemberShowProps {
  member: MemberDetail;
  available_groups: GroupOption[];
  organization: Organization;
  magic_link?: string | null;
}

function groupLabel(groupKey: string) {
  return groupKey.replace(/_/g, " ").toUpperCase();
}

export default function MemberShow({
  member,
  available_groups: availableGroups,
  magic_link: magicLink,
}: MemberShowProps) {
  const routes = useOrganizationRoutes();
  const { auth } = usePage<SharedData>().props;
  const canManage = auth.groups.includes("owner") || auth.groups.includes("admin");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setCopied(false);
  }, [magicLink]);

  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Miembros", href: routes.members.index() },
    { title: member.full_name ?? member.name ?? member.email, href: "#" },
  ];

  const initialGroupKeys = useMemo(() => member.groups.slice().sort(), [member.groups]);
  const { data, setData, processing, errors, transform, recentlySuccessful, isDirty, put } = useForm({
    group_keys: initialGroupKeys,
  });

  useEffect(() => {
    setData("group_keys", initialGroupKeys);
  }, [initialGroupKeys, setData]);

  const toggleGroup = (groupKey: string) => {
    const currentKeys = Array.isArray(data.group_keys) ? data.group_keys : [];
    if (currentKeys.includes(groupKey)) {
      setData(
        "group_keys",
        currentKeys.filter((key) => key !== groupKey)
      );
    } else {
      setData("group_keys", [...currentKeys, groupKey]);
    }
  };

  const handleUpdate = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!isDirty) return;
    transform((formData) => ({
      member: formData,
    }));
    put(routes.members.show(member.id));
  };

  const handleRemove = () => {
    if (!window.confirm("¿Seguro que querés remover este miembro de la organización?")) {
      return;
    }
    router.delete(routes.members.show(member.id));
  };

  const handleMagicLink = () => {
    router.post(`${routes.members.show(member.id)}/magic_link`);
  };

  const handleCopy = async () => {
    if (!magicLink) return;
    try {
      await navigator.clipboard.writeText(magicLink);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Miembro: ${member.full_name ?? member.name ?? member.email}`} />
      <div className="flex h-full flex-1 flex-col gap-6 p-6">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <Button variant="ghost" size="sm" asChild className="mb-2">
              <Link href={routes.members.index()}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Volver a Miembros
              </Link>
            </Button>
            <h1 className="text-3xl font-bold">
              {member.full_name ?? member.name ?? member.email}
            </h1>
            <p className="text-muted-foreground">{member.email}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {member.groups.map((group) => (
              <Badge key={group} variant="regular" color="sky">
                {groupLabel(group)}
              </Badge>
            ))}
          </div>
        </div>

        {magicLink && (
          <Alert className="border-sky-200 bg-sky-50 text-sky-950">
            <AlertTitle>Link de acceso generado</AlertTitle>
            <AlertDescription className="space-y-3 text-sky-950">
              <p>Copiá y compartí este link con el miembro.</p>
              <div className="flex flex-col gap-3 rounded-md border border-sky-200 bg-white/60 p-3 md:flex-row md:items-center md:justify-between">
                <code className="text-sm break-all">{magicLink}</code>
                <Button
                  type="button"
                  variant="outline"
                  className="border-sky-300 bg-white hover:bg-sky-100"
                  onClick={handleCopy}
                >
                  <Copy className="mr-2 h-4 w-4" />
                  {copied ? "Copiado" : "Copiar"}
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Roles asignados</CardTitle>
            <CardDescription>
              Definí los roles del miembro dentro de esta organización.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleUpdate} className="space-y-4">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {availableGroups.map((group) => {
                  const checked = data.group_keys.includes(group.key);
                  return (
                    <label
                      key={group.key}
                      className="flex items-center gap-3 rounded-lg border border-border/70 bg-muted/10 p-3"
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => toggleGroup(group.key)}
                        disabled={!canManage || processing}
                      />
                      <div>
                        <p className="text-sm font-medium leading-none">
                          {group.name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {group.key}
                        </p>
                      </div>
                    </label>
                  );
                })}
              </div>
              <InputError message={errors.group_keys} />
              {canManage && (
                <div className="flex items-center gap-3">
                  <Button type="submit" disabled={!isDirty || processing}>
                    Guardar roles
                  </Button>
                  {recentlySuccessful && (
                    <span className="text-sm text-muted-foreground">
                      Roles actualizados.
                    </span>
                  )}
                </div>
              )}
            </form>
          </CardContent>
        </Card>

        {canManage && (
          <Card>
            <CardHeader>
              <CardTitle>Acceso</CardTitle>
              <CardDescription>
                Generá un link de acceso rápido para este miembro.
              </CardDescription>
            </CardHeader>
            <CardFooter>
              <Button variant="outline" onClick={handleMagicLink} className="gap-2">
                <LinkIcon className="h-4 w-4" />
                Generar link
              </Button>
            </CardFooter>
          </Card>
        )}

        {canManage && (
          <Card>
            <CardHeader>
              <CardTitle>Zona peligrosa</CardTitle>
              <CardDescription>
                Eliminá al miembro de la organización si ya no necesita acceso.
              </CardDescription>
            </CardHeader>
            <CardFooter>
              <Button variant="destructive" onClick={handleRemove} className="gap-2">
                <Trash2 className="h-4 w-4" />
                Remover miembro
              </Button>
            </CardFooter>
          </Card>
        )}
      </div>
    </AppLayout>
  );
}
